import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { ServicesBuilder } from '../utils/setup';
import {
  AppConfig,
  CalcFlow,
  CalcType,
  Customer,
  ICustomer,
  IInvoice,
  IInvoiceCoreLine,
  IInvoiceLine,
  IInvoiceOperationService,
  IItem,
  IPaymentMethod,
  IPaymentService,
  ISectionItem,
  ITill,
  InvoiceOperationService,
  InvoiceService,
  PaymentService,
  Till,
  Config,
  LineOperationService,
  IChangePaymentMethodOption,
  IEmployee,
  InvoiceCoreLine,
  IInvoiceService,
  DeviceType,
  Invoice,
  InvoiceDeviceType,
  InvoiceStatus,
  SystemError,
  InvoiceType,
  ModelPrefix,
  Item,
  InvoiceAction,
  PrinterType,
  SectionItemStatus,
} from '@posx/core';
import { Subscription } from 'dexie';
import { DataWorker } from '@/workers/data.worker';
import { HubConnectionState } from '@microsoft/signalr';
import { useDevice } from './device.store';
import { captureException } from '@sentry/vue';
import { useConfigStore } from './config';
import { usePrinterStore } from './printer';
import { logError, logInfo, logLiveQueryError, serializeError, isLiveQueryCursorError } from '@/utils/logger';
import { notify } from '@/utils/notification';
import { i18n } from '@/main';
import cloneDeep from 'lodash/cloneDeep';
import remove from 'lodash/remove';
import uniq from 'lodash/uniq';
import find from 'lodash/find';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import findIndex from 'lodash/findIndex';
import { useSectionStore } from './section';
import { useProductStore, getPriceByPlu, getOrderPrice, isPromoAvailable, getPromoMaxQty } from './product';
// Mirrors @posx/core's protected compareModifiers, needed to pre-empt its merge decision before calling addItemToLine
const sameModifiers = (a: IInvoiceCoreLine[] = [], b: IInvoiceCoreLine[] = []): boolean =>
  a.length === b.length && a.every(m => b.some(n => n.item_uid === m.item_uid && n.price === m.price && n.quantity === m.quantity));
// Auto modifiers ride along 1 per product unit: always-auto (e.g. BCRS deposit) on every order type, standalone or inside a combo; is_auto_applied (e.g. takeaway box) only on takeaway when enable_takeaway_auto_modifier is on
export const autoAppliedModifiers = (item: IItem, invoice: IInvoice, service: IInvoiceService, isTakeaway = false): IInvoiceCoreLine[] =>
  (item.modifiers ?? []).filter(m => (m.is_always_auto_applied || isTakeaway && m.is_auto_applied && useConfigStore().appConfig?.config?.order?.enable_takeaway_auto_modifier) && !m.is_sold_out).map(m => Object.assign(service.toInvoiceCoreLine(m, 1), { price: getPriceByPlu(m, invoice.price_lookup_key ?? '') }));
// Import CRM store for customer info refresh
import { useWyoSdkStore } from '@xpos/crm/src/stores/data';
// Import wyocrm types
import { ICustomer as WyoICustomer } from '@wyocrm/sdk';
import { useStockLogStore } from './stockLog';
import { nanoid } from 'nanoid';
import { preciseRound } from '@/utils';
import { QueryBuilder, QueryResponse, SortOrder } from '@litepos/autoquery';
import dayjs from 'dayjs';

export const useInvoiceStore = defineStore('invoice', {
  state: () => ({
    service: null as IInvoiceService | null,
    currentInvoice: new Invoice(InvoiceDeviceType.Posx),
    _subscription: null as Subscription | null,
    _lqSeq: 0, // sequence number to discard superseded emissions
    paymentInProgress: false, // integrated payment dialog open, send_order defers this table (#1703)
    payAtCounterCount: 0, // live count of pay_at_counter invoices for the Manage/Invoices badges
    _payAtCounterSub: null as Subscription | null,
  }),
  actions: {
    async getService(getNewInstance: boolean = false): Promise<IInvoiceService> {
      if (this.service && !getNewInstance) {
        return this.service;
      } else {
        // service singleton is method-only, skip deep proxy
        const service = markRaw(await ServicesBuilder.getInstance().getService<IInvoiceService>(InvoiceService));
        if(!getNewInstance) this.service = service;
        return service;
      }
    },

    /**
     * Refresh customer information from CRM system and update invoice
     * @param invoice - Invoice to update customer info
     */
    async getCustomer(customerId: number) : Promise<ICustomer | null> {
      if (!customerId || customerId === 0) return null;
      // Gracefully handle API failures - payment should proceed even without customer data
      try {
        const wyoSdkStore = useWyoSdkStore();
        const params = new QueryBuilder<WyoICustomer>();
        params.addParam('id', 'EqualTo', customerId);
        const response = await wyoSdkStore.queryCustomers(params);
        return response.results?.[0] as unknown as ICustomer ?? null;
      } catch (e: any) { logError('getCustomer failed', serializeError(e)); return null; }
    },

    // Subscribe to invoice changes from IndexedDB for real-time updates
    async startCurrentInvoiceLiveQuery(uid: string) {
      if (!uid) throw new SystemError('Invoice uid is required');
      // unsubscribe from old invoice if UID changed
      if (this._subscription && this.currentInvoice?.uid !== uid) {
        this._subscription.unsubscribe();
        this._subscription = null;
      }
      if (this._subscription) return;
      const service = await this.getService();
      if (this._subscription) return; // re-check after await to close TOCTOU race window
      const sub: Subscription = service
        .liveQuery(() => service.getOne(uid))
        .subscribe({
          next: async (result: IInvoice) => {
            if (!result) return;
            if (result.status === InvoiceStatus.Paid || result.status === InvoiceStatus.Void) return;
            // opt-in: when off, fall through to the plain replace behaviour
            if (useConfigStore().appConfig?.config?.order?.keep_unsent_lines) {
              const seq = ++this._lqSeq;
              // recalc before publish so UI/CDS never see grafted lines with stale totals; drop emission on calc failure
              if (service.rebaseUnsentLines(result, this.currentInvoice)) {
                try { await this.calculate(result); logInfo('rebase grafted unsent lines', { invoiceUid: result.uid }); }
                catch (e) { return logError('rebase recalc failed', serializeError(e)); }
              }
              if (seq !== this._lqSeq) return; // superseded by newer emission
            }
            this.currentInvoice = result;
            if (result.id_in_server) this.syncInvoice(result); // CDS sync on liveQuery update
          },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'invoice', query: 'currentInvoice' }); if (isLiveQueryCursorError(err) && this._subscription === sub) { sub.unsubscribe(); this._subscription = null; } }
        });
      this._subscription = markRaw(sub);
    },
    stopCurrentInvoiceLiveQuery() {
      this._subscription?.unsubscribe();
      this._subscription = null;
    },
    // Subscribe to the pay_at_counter invoice count so the badges update without refresh
    async startPayAtCounterCountLiveQuery() {
      const service = await this.getService();
      if (this._payAtCounterSub) return; // checked after await so concurrent callers subscribe once
      const sub: Subscription = service.liveQuery(() => service.table.where('status').equals(InvoiceStatus.PayAtCounter).count())
        .subscribe({
          next: (count: number) => { this.payAtCounterCount = count; },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'invoice', query: 'payAtCounterCount' }); if (isLiveQueryCursorError(err) && this._payAtCounterSub === sub) { sub.unsubscribe(); this._payAtCounterSub = null; } }
        });
      this._payAtCounterSub = markRaw(sub);
    },
    /**
     * Set the ref_id for the current invoice.
     */
    async setRefId() {
      const invoiceService = await this.getService();
      if (!this.currentInvoice) this.currentInvoice = new Invoice(InvoiceDeviceType.Posx);
      if (!this.currentInvoice.ref_id) this.currentInvoice.ref_id = await invoiceService.getRefId();
    },
    // sectionItemOverride: the invoice's own table, when it differs from the currently viewed table (e.g. transfer target)
    async calculate(invoice: IInvoice, disableRounding = true, sectionItemOverride?: ISectionItem) {
      const service = await this.getService();
      const configStore = useConfigStore();
      const sectionStore = useSectionStore();
      await configStore.startLiveQuery();
      const appConfig = configStore.appConfig;
      if(!appConfig) throw new SystemError('System config not available');
      // sync payment_has_cash: preserve caller's flag, or derive from cashless config / actual payments
      invoice.payment_has_cash = invoice.payment_has_cash || !!appConfig.config.rounding.cashless || !!invoice?.payments?.some((payment) => payment.payment_method?.is_cash === true);
      if(invoice.payment_has_cash) disableRounding = false;
      this.setInvoiceType(invoice, sectionStore.currentSectionItem);
      this.calculateTotalItemQuantity(invoice);
      await this.reconcileAutoAppliedItems(invoice, sectionItemOverride ?? sectionStore.currentSectionItem);
      if (appConfig.config?.order?.keep_auto_added_lines_at_end) {
        const auto: IInvoiceLine[] = [], rest: IInvoiceLine[] = [];
        for (const l of invoice.lines) (l.is_auto_applied || l.is_pax_linked ? auto : rest).push(l);
        invoice.lines = [...rest, ...auto];
      }
      return service.calculate(invoice, appConfig, disableRounding);
    },
    async changeQuantity(invoice: IInvoice, line: IInvoiceLine, quantity: number, type: number) {
      const service = await this.getService();
      if (type === 1) {
        return service.addLineQuantity(invoice, line, quantity);
      } else {
        return service.minusLineQuantity(invoice, line, quantity);
      }
    },
    async addItemToLine(
      invoice: IInvoice,
      data: { item: IItem; quantity?: number; price?: number; modifiers?: IInvoiceCoreLine[]; remark?: string; isPromo?: boolean },
      createNewLine: boolean = false,
      customer: ICustomer = new Customer(),
    ) {
      const { item, quantity, price, modifiers = [], remark = '', isPromo = false } = data;
      const service = await this.getService();
      const configStore = useConfigStore();
      await configStore.startLiveQuery();
      const appConfig = configStore.appConfig;
      if(!appConfig) throw new SystemError('System config not available');
      const noCrossSend = appConfig.config?.order?.disable_line_merge_between_sends;
      // Cross-send merge disabled: reuse the still-unsent line for this exact product ourselves (core's own search always
      // prefers the first matching line regardless of sent status, which would silently re-merge into an already-sent line)
      // Promo item: core matches by price alone, so only reuse a line with the same promo flag, else force a new line
      if(!createNewLine && (noCrossSend || getPromoMaxQty(item))) {
        const effectivePrice = price ?? item.price;
        const openLine = invoice.lines?.find(l => l.item_uid === item.uid && !l.is_combo_item && !l.is_take_out && !(noCrossSend && l.sent)
          && l.remark === remark && l.price === effectivePrice && !l.meta?.is_promo_price === !isPromo && sameModifiers(l.modifiers, modifiers));
        if(openLine) { service.addLineQuantity(invoice, openLine, quantity ?? 1); return openLine; }
        createNewLine = true;
      }
      const line = service?.addItemToLine(invoice, customer, appConfig, item, modifiers, {
        quantity,
        price,
        remark,
      }, createNewLine);
      // Promo lines carry a persisted mark: quota, tier switches and merges recognise them by it, never by price
      if(isPromo) line.meta = { ...line.meta, is_promo_price: true };
      return line;
    },
    // Shared "add a product" logic (clone + PLU pricing) used by both manual menu selection and auto-applied items
    async addProductToInvoice(invoice: IInvoice, item: IItem, quantity: number = 1, createNewLine: boolean = false, isTakeaway = false) {
      const itemToAdd = cloneDeep(item);
      itemToAdd.meta ??= {};
      (itemToAdd.meta as Record<string, any>).local_original_price ??= item.price;
      const isPromo = isPromoAvailable(item, invoice);
      itemToAdd.price = getOrderPrice(item, invoice);
      const service = await this.getService();
      const modifiers = autoAppliedModifiers(itemToAdd, invoice, service, isTakeaway);
      return this.addItemToLine(invoice, { item: itemToAdd, quantity, price: itemToAdd.price, modifiers, isPromo }, createNewLine);
    },

    async addComboItemToLine(
      invoice: IInvoice,
      data: {
        item: IItem;
        comboLineUid: string;
        comboGroupUid: string;
        quantity?: number;
        price?: number;
        modifiers?: IInvoiceCoreLine[];
        remark?: string
      },
      customer: ICustomer = new Customer(),
    ) {
      const { item, comboLineUid, comboGroupUid, quantity, price, modifiers: passedModifiers = [], remark = '' } = data;
      // Ensure price is number type
      const safePrice = price !== undefined ? Number(price) : undefined;
      const service = await this.getService();
      const configStore = useConfigStore();
      await configStore.startLiveQuery();
      const appConfig = configStore.appConfig;
      if(!appConfig) throw new SystemError('System config not available');

      // Skip modifiers already carried over (e.g. table transfer) so always-auto ones aren't added twice
      const autoModifiers = autoAppliedModifiers(item, invoice, service).filter(m => !passedModifiers.some(p => p.item_uid === m.item_uid));
      const modifiers = [...passedModifiers, ...autoModifiers];
      const result = await service?.addComboItemToLine(invoice, customer, appConfig, item, comboLineUid, comboGroupUid, modifiers, {
        quantity,
        price: safePrice,
        remark,
      });

      // Sort combo items according to combo group order
      this.sortComboItems(invoice, comboLineUid);
      result.selected = true;
      return result;
    },
    async changeModifierQuantity(invoice: IInvoice | undefined, line: IInvoiceLine, modifier: IInvoiceCoreLine, quantity = 0, type: number) {
      // invoice + line let core keep a removed sent modifier in deleted_modifiers so its stock is returned
      return (await this.getService())?.changeModifierQuantity(line.modifiers, modifier, type === 1 ? quantity : -quantity, invoice, line);
    },
    async duplicateLine(invoice: IInvoice, line: IInvoiceLine) {
      const service = await this.getService();
      await service.duplicateLine(invoice, line);
      return invoice?.lines[invoice?.lines.length - 1];
    },
    splitInvoiceLine(line: IInvoiceLine): IInvoiceLine[] {
        const newLines: IInvoiceLine[] = [];
        for(let i = 0; i < line.quantity; i++) {
          const newLine = cloneDeep(line);
          newLine.quantity = 1;
          if(line.sent > 0) { newLine.sent = 1; line.sent-- }
          if(line.kds_done > 0) { newLine.kds_done = 1; line.kds_done-- }
          if((line.kds_prepared ?? 0) > 0) { newLine.kds_prepared = 1; line.kds_prepared = line.kds_prepared! - 1; }
          if((line.kds_batch ?? 0) > 0) { newLine.kds_batch = 1; line.kds_batch = line.kds_batch! - 1; }
          newLine.meta = {
            ...line.meta,
            parent_line_uid: line.uid,
          }
          newLine.uid = ModelPrefix.InvoiceLine + nanoid();
          newLines.push(newLine);
        }
        return newLines;
      },
    // When cross-send merge is disabled, an already-sent line only merges with a same-round line (same created_at)
    shouldPreventCrossRoundMerge(a: IInvoiceLine, b: IInvoiceLine): boolean {
        if(!useConfigStore().appConfig?.config?.order?.disable_line_merge_between_sends) return false;
        if(!a.sent && !b.sent) return false;
        // created_at may arrive as a Date instance or an ISO string (e.g. after a JSON round-trip), so normalize before comparing
        return new Date(a.created_at).getTime() !== new Date(b.created_at).getTime();
    },
    // Per-unit mod.sent can't represent two lines whose modifiers were sent differently, so they must not merge
    hasSameModifierSent(a: IInvoiceLine, b: IInvoiceLine): boolean {
        return (a.modifiers || []).every(m => (b.modifiers?.find(x => x.item_uid === m.item_uid && x.price === m.price)?.sent || 0) === (m.sent || 0));
    },
    // Promo and regular lines never merge: core compareLines ignores meta, so a same-priced regular line would swallow the promo mark
    hasSamePromoFlag(a: IInvoiceLine, b: IInvoiceLine): boolean {
        return !a.meta?.is_promo_price === !b.meta?.is_promo_price;
    },
    async mergeInvoiceLine(siblingLines: IInvoiceLine[], targetLine: IInvoiceLine): Promise<IInvoiceLine> {
        if(siblingLines?.length === 0) return targetLine;
        if(!targetLine?.meta?.parent_line_uid) return targetLine;
        const areAllSiblingLines = siblingLines.every(line => line?.meta?.parent_line_uid === targetLine.meta?.parent_line_uid);
        if(!areAllSiblingLines) throw new SystemError('Lines properties are not same');

        const service = await this.getService();
        let sameSiblingLine = siblingLines.find(line => !this.shouldPreventCrossRoundMerge(line, targetLine) && service.compareLines(line, targetLine) && this.hasSameModifierSent(line, targetLine) && this.hasSamePromoFlag(line, targetLine));
        if(sameSiblingLine) {
            sameSiblingLine.quantity += targetLine.quantity;
            sameSiblingLine.sent += targetLine.sent;
            sameSiblingLine.kds_done += targetLine.kds_done;
            sameSiblingLine.kds_prepared = (sameSiblingLine.kds_prepared ?? 0) + (targetLine.kds_prepared ?? 0);
            sameSiblingLine.kds_batch = (sameSiblingLine.kds_batch ?? 0) + (targetLine.kds_batch ?? 0);
            return sameSiblingLine;
        }
        return targetLine;
    },
    async mergeInvoiceLines(invoice: IInvoice, linesToMerge: IInvoiceLine[]): Promise<void> {
        if (!linesToMerge?.length) return;
        if (linesToMerge.length === 1) return ;
        const targetLine = linesToMerge[linesToMerge.length - 1];
        const siblingLines = linesToMerge.filter(line => line !== targetLine);
        const mergedLine = await this.mergeInvoiceLine(siblingLines, targetLine);
        if(mergedLine !== targetLine) {
            remove(linesToMerge, (l) => l === targetLine);
            remove(invoice.lines, (l) => l === targetLine);
            await this.mergeInvoiceLines(invoice, linesToMerge);
        }
    },
    clearParentLineUidByLine(invoice: IInvoice, parentLineUid: string): void {
        if(!parentLineUid) return;
        for(const line of invoice.lines) {
            if(line?.meta?.parent_line_uid === parentLineUid) delete line.meta?.parent_line_uid;
        }
    },
    async mergeSiblingLines(invoice: IInvoice, parentLineUid: string): Promise<IInvoiceLine | undefined> {
        const siblingLines = invoice.lines.filter(l => l.meta?.parent_line_uid === parentLineUid);
        await this.mergeInvoiceLines(invoice, siblingLines);
        this.clearParentLineUidByLine(invoice, parentLineUid);
        await this.calculate(invoice);
        return invoice.lines.find(l => siblingLines.includes(l));
    },
    // Reorder modifier sequences in invoice lines matching itemUid
    async reorderLineModifierSequences(invoice: IInvoice, itemUid: string, sequenceMap: Map<string, number>): Promise<void> {
        if (!invoice?.lines) return;
        invoice.lines.forEach(line => {
            if (line.item?.uid !== itemUid) return;
            (line.item as any).modifiers?.forEach((mod: any) => { const seq = sequenceMap.get(mod.uid); if (seq !== undefined) mod.sequence = seq; });
            line.modifiers?.forEach(mod => { const seq = sequenceMap.get((mod.item as any)?.uid); if (seq !== undefined) (mod.item as any).sequence = seq; });
        });
        await this.calculate(invoice);
    },
    unselectAllLines(lines: IInvoiceLine[]): void {
        lines.forEach(line => line.selected = false);
    },
    async mergeAllLines(invoice: IInvoice): Promise<void> {
        if(!invoice.lines?.length) return;
        const service = await this.getService();
        const distinctParentLineUids = uniq(invoice.lines.map(line => line?.meta?.parent_line_uid)).filter(Boolean).reverse();
        for (const parentLineUid of distinctParentLineUids) {
            const siblingLines = invoice.lines.filter(l => l.meta?.parent_line_uid === parentLineUid);
            await this.mergeInvoiceLines(invoice, siblingLines);
            this.clearParentLineUidByLine(invoice, parentLineUid);
        }
        // merge non-combo lines by compareLines
        const merged: IInvoiceLine[] = [];
        for (const line of invoice.lines) {
            if (line.is_combo || line.is_combo_item) { merged.push(line); continue; }
            const sameLine = merged.find(l => !l.is_combo && !l.is_combo_item && l.item_uid === line.item_uid && !this.shouldPreventCrossRoundMerge(l, line) && service.compareLines(l, line) && this.hasSameModifierSent(l, line) && this.hasSamePromoFlag(l, line));
            if (sameLine) { sameLine.quantity += line.quantity; sameLine.sent += line.sent; sameLine.kds_done += line.kds_done; sameLine.kds_prepared = (sameLine.kds_prepared ?? 0) + (line.kds_prepared ?? 0); sameLine.kds_batch = (sameLine.kds_batch ?? 0) + (line.kds_batch ?? 0); }
            else merged.push(line);
        }
        invoice.lines = merged;
    },
    async createOrUpdateInvoice(invoice: IInvoice, sectionItem: ISectionItem, employee?: IEmployee) {
      const service = await this.getService();
      await this.mergeAllLines(invoice);
      await this.calculate(invoice);
      this.unselectAllLines(invoice.lines);
      // Update line.updated_at for unsent lines (for KDS display)
      const now = new Date();
      for (const line of invoice.lines)
        if (line.quantity !== line.sent) line.updated_at = now;
      sectionItem.invoice_created_at = invoice.created_at;
      if (invoice.id_in_server) return await service.updateInvoice(invoice, sectionItem, employee);
      else { invoice.meta = {...invoice.meta, is_sent: true}; return await service.createInvoice(invoice, sectionItem, employee); }
    },
    async payInvoice(invoice: IInvoice, sectionItem: ISectionItem, till?: ITill, employee?: IEmployee) {
      try {
        const printerStore = usePrinterStore();
        const service = await this.getService();
        const terminalPrinter = await printerStore.getTerminalPrinter(PrinterType.ReceiptPrinter);
        const clonedSectionItem = cloneDeep(sectionItem);
        clonedSectionItem.invoice_created_at = null;
        if (invoice.id_in_server && invoice.action !== InvoiceAction.PayAtCounter) await service.payInvoice(invoice, clonedSectionItem, till, employee, terminalPrinter || undefined);
        else {
          invoice.table_uid = sectionItem.uid;
          invoice.table_name = sectionItem.name;
          await service.directPayInvoice(invoice, till, employee, terminalPrinter || undefined);
          // Reset table state - directPayInvoice has no sectionItem param
          const sectionItemService = await useSectionStore().getSectionItemService();
          const fresh = await sectionItemService.getOne(sectionItem.uid);
          if (fresh && fresh.status !== SectionItemStatus.CLOSED)
            await sectionItemService.updateOne(fresh.id_in_server!, { ...fresh, invoice_uid: '', status: SectionItemStatus.CLOSED });
        }
        await useStockLogStore().recordSalesLogs(invoice, employee?.uid);
      } catch (e: any) {
        // Stock sync 5xx/network failure after payment should not block payment success
        if ((e?.config?.url?.includes('/items') || e?.config?.url?.includes('/stock_logs/stock_log')) && (!e?.response || e.response.status >= 500)) {
          captureException(e);
          logError('Stock sync failed after payment succeeded', serializeError(e));
          if (useConfigStore().appConfig?.config?.inventory?.inventory_control)
            notify.warning(i18n.global.t('invoice.stockSyncFailed'));
          return;
        }
        captureException(e);
        logError('Error paying invoice', serializeError(e));
        throw new SystemError(`Error paying invoice: ${e.message}`);
      }
    },
    async autoQuery(
      options: { keywords?: string; status?: string; dateTime?: string },
      page: number = 1,
      pageSize: number = 10,
    ): Promise<QueryResponse<IInvoice>> {
      const service = await this.getService();
      // options.take = 10;
      // options.skip = (page - 1) * 10;
      // options.orderByDesc = 'id';

      const queryBuilder = new QueryBuilder<IInvoice & { id: number }>();
      queryBuilder.sortBy('id', SortOrder.Desc);
      queryBuilder.includeTotal(true);
      if (options.keywords?.trim()) {
        queryBuilder.addRawParam('search_keywords', options.keywords.trim());
      }
      if (options.status) {
        queryBuilder.addParam('status', 'Contains', options.status as string);
      }
      // Filter invoices by selected date (full day range) - use UTC/ISO format
      if (options.dateTime) {
        const date = dayjs(options.dateTime);
        queryBuilder.addParam('created_at', 'GreaterThanOrEqualTo', date.startOf('day').toISOString());
        queryBuilder.addParam('created_at', 'LessThan', date.endOf('day').add(1, 'second').toISOString());
      }
      queryBuilder.take(pageSize);
      queryBuilder.skip((page - 1) * pageSize);
      return service?.autoQuery(queryBuilder) ?? new QueryResponse<IInvoice>();
    },
    async reprintLastReceipt(tableUid: string) {
      const service = await this.getService();
      const printerStore = usePrinterStore();
      let invoice = await service.db
        .table('invoices')
        .where('table_uid')
        .equals(tableUid)
        .and((x) => x.status === InvoiceStatus.Paid)
        .filter((x) => x.deleted_at === null || x.deleted_at === undefined)
        .sortBy('paid_at')
        .then(arr => arr[arr.length - 1]);
      const terminalPrinter = await printerStore.getTerminalPrinter(PrinterType.ReceiptPrinter);
      if (invoice) await service.reprintReceipt(invoice, false, terminalPrinter || undefined);
    },
    /**
     * Sync latest invoice status to customer display system.
     *
     * @param invoice - The invoice data to send.
     * @returns A Promise that resolves when the invoice is sent successfully.
     */
    async syncInvoice(invoice: IInvoice) {
      const configStore = useConfigStore();
      const appConfig = configStore.appConfig;
      if(!appConfig?.config?.cds?.enabled) return;
      try {
        const deviceStore = useDevice();
        const cdsUid = await deviceStore.getCdsUid();
        const connection = DataWorker.getInstance().getConnection();
        if (connection.state === HubConnectionState.Connected && cdsUid)
          await connection.send('sync_invoice', cdsUid, invoice); // await so send rejection hits the catch instead of an unhandled rejection
      } catch (e: any) {
        logError('Failed to sync invoice to CDS', serializeError(e));
      }
    },

    async checkInvoice(invoice: IInvoice, sectionItem: ISectionItem, employee?: IEmployee) {
      const service = await this.getService();
      const printerStore = usePrinterStore();
      if (!invoice.id_in_server) {
        await service.createInvoice(invoice, sectionItem, employee);
        const deadline = Date.now() + 10000;
        while (!invoice.id_in_server && Date.now() < deadline) {
          await new Promise(r => setTimeout(r, 1000));
          invoice = await service.getOne(invoice.uid) ?? invoice;
        }
        if (!invoice.id_in_server) throw new SystemError('Network is slow, unable to receive latest invoice data');
        sectionItem = await (await useSectionStore().getSectionItemService()).getOne(sectionItem.uid) ?? sectionItem;
      }
      const terminalPrinter = await printerStore.getTerminalPrinter(PrinterType.CheckPrinter);
      return await service.checkInvoice(invoice, sectionItem, employee, terminalPrinter || undefined);
    },

    /**
     * Set invoice type based on section item
     * @param invoice - The invoice to set type for
     * @param sectionItem - The section item to determine invoice type
     * @returns The invoice with updated type
     */
    setInvoiceType(invoice: IInvoice, sectionItem?: ISectionItem): void {
      invoice.type = sectionItem?.is_take_out ? InvoiceType.TakeOut : InvoiceType.DineIn;
    },

    calculateTotalItemQuantity(invoice: IInvoice): void {
      let totalQuantity = 0;
      if(!invoice.lines || invoice.lines.length == 0 ) return;
      for(const line of invoice.lines) {
        if(line.quantity) totalQuantity += line.quantity;
      }
      if(!invoice.meta) invoice.meta = {};
      invoice.meta.total_item_quantity = totalQuantity;
    },
    // Check if all required modifiers are selected for a single line
    isLineModifiersComplete(line: IInvoiceLine): true | { groupName: string } {
      if (!line.item?.modifier_categories) return true;
      for (const cat of line.item.modifier_categories) {
        const minOrder = cat.min_order ?? 0;
        if (minOrder <= 0) continue;
        const selectedCount = (line.modifiers || [])
          .filter(mod => line.item?.modifiers?.find(m => m.uid === mod.item_uid)?.category_uid === cat.uid)
          .reduce((sum, mod) => sum + (mod.quantity || 0), 0);
        if (selectedCount < minOrder) return { groupName: (cat as any).name_translations?.[i18n.global.locale.value || 'en'] || cat.name };
      }
      return true;
    },
    // Checks the currently selected line's modifier completeness with warning (gated by check_modifier_min_order)
    checkCurrentLineModifierCompleteness(): boolean {
      if (!useConfigStore().appConfig?.config?.order?.check_modifier_min_order) return true;
      if (this.currentInvoice.action === InvoiceAction.PayAtCounter) return true;
      const line = this.currentInvoice.lines.find(l => l.selected);
      if (!line || line.item?.type === 'combo' || !line.item?.modifier_categories) return true;
      const result = this.isLineModifiersComplete(line);
      if (result === true) return true;
      const productName = line.item?.name_translations?.[i18n.global.locale.value] || line.item?.name || '';
      notify.warning(i18n.global.t('table.incomplete_modifier', { product: productName, group: result.groupName }));
      return false;
    },

    /**
     * Check if all combo lines in the invoice are complete
     * @returns true if all combos are complete, or the uid of the first incomplete combo
     */
    isAllComboComplete(): true | IInvoiceLine {
      for (const line of this.currentInvoice.lines) {
        if (line.item?.type === 'combo' && line.item?.combo_groups) {
          if (!this.isComboComplete(line)) {
            return line;
          }
        }
      }
      return true;
    },

    /**
     * Check if a single combo line is complete
     * @param comboLine - The combo line to check
     * @param invoice - Optional invoice to check against, defaults to currentInvoice
     * @returns true if the combo line is complete, false otherwise
     */
    isComboComplete(comboLine: IInvoiceLine, invoice?: IInvoice): boolean {
      const targetInvoice = invoice || this.currentInvoice;

      if (!comboLine.item?.combo_groups) return true;

      // Count combo items by group
      const comboItemsByGroup = targetInvoice.lines.reduce((acc, line) => {
        if (line.is_combo_item && line.combo_line_uid === comboLine.uid && line.combo_group_uid) {
          acc[line.combo_group_uid] = (acc[line.combo_group_uid] || 0) + (line.quantity || 0);
        }
        return acc;
      }, {} as Record<string, number>);

      // Check each combo group requirements
      return comboLine.item.combo_groups.every(comboGroup => {
        const selectedCount = comboItemsByGroup[comboGroup.uid] || 0;
        const minOrder = comboGroup.min_order ?? 0;
        const maxOrder = comboGroup.max_order ?? 0;

        // Check minimum requirement (if minOrder > 0)
        const meetsMinRequirement = minOrder === 0 || selectedCount >= minOrder;

        // Check maximum requirement (if maxOrder > 0)
        const meetsMaxRequirement = maxOrder === 0 || selectedCount <= maxOrder;

        return meetsMinRequirement && meetsMaxRequirement;
      });
    },

    /**
     * Sort combo items according to combo group order
     * @param invoice - The invoice containing the lines
     * @param comboLineUid - The UID of the combo line
     */
    sortComboItems(invoice: IInvoice, comboLineUid: string): void {
      const comboLine = find(invoice.lines, { uid: comboLineUid });
      if (!comboLine?.item?.combo_groups) return;

      const comboItems = filter(invoice.lines, {
        is_combo_item: true,
        combo_line_uid: comboLineUid
      });

      if (isEmpty(comboItems)) return;

      // Sort combo items by group array position
      const sortedComboItems = orderBy(comboItems, [
        item => findIndex(comboLine.item!.combo_groups!, { uid: item.combo_group_uid }) ?? 999
      ]);

      // Remove and re-insert sorted combo items
      remove(invoice.lines, (line: IInvoiceLine) => line.is_combo_item && line.combo_line_uid === comboLineUid);

      const comboLineIndex = findIndex(invoice.lines, { uid: comboLineUid });
      if (comboLineIndex !== -1) {
        invoice.lines.splice(comboLineIndex + 1, 0, ...sortedComboItems);
      }
    },

    // Keep auto-applied packing-fee lines in sync with take-out state; re-run on every calculate() so it self-heals instead of relying on being triggered from every call site
    async reconcileAutoAppliedItems(invoice: IInvoice, sectionItem: ISectionItem | undefined) {
      // Filter out auto-applied lines to prevent packing fee self-referencing via its own is_take_out
      const regularLines = invoice.lines.filter(l => !l.is_auto_applied);
      const hasValidProducts = regularLines.some(l => l.quantity > 0);
      const shouldHave = hasValidProducts && (sectionItem?.is_take_out || regularLines.some(l => l.is_take_out));
      const hasAutoLine = invoice.lines.some(l => l.is_auto_applied);
      if (shouldHave === hasAutoLine) return;
      if (shouldHave) {
        const productStore = useProductStore();
        for (const item of productStore.items.filter(i => i.is_auto_applied)) {
          const newLine = await this.addProductToInvoice(invoice, item);
          if (newLine) newLine.is_auto_applied = true;
        }
      } else {
        // auto line is always a single line per product (hasAutoLine gate), so just deduct one unit — no full merge, keeps split lines intact
        const service = await this.getService();
        for (const line of invoice.lines.filter(l => l.is_auto_applied)) {
          service.minusLineQuantity(invoice, line, 1);
          line.is_auto_applied = false;
        }
      }
    },

    // Adjust pax-linked product quantities to match pax count (e.g. seat fee)
    async reconcilePaxLinkedItems(invoice: IInvoice, newPax: number) {
      const productStore = useProductStore();
      const service = await this.getService();
      for (const item of productStore.items.filter(i => i.is_pax_linked)) {
        const existingLine = invoice.lines.find(l => l.item_uid === item.uid && l.is_pax_linked);
        const currentQty = existingLine?.quantity ?? 0;
        const targetQty = Math.ceil(newPax / Number(item.is_pax_linked));
        const diff = targetQty - currentQty;
        if (diff > 0) {
          if (existingLine) service.addLineQuantity(invoice, existingLine, diff);
          else {
            const newLine = await this.addProductToInvoice(invoice, item);
            if (newLine) { newLine.is_pax_linked = true; if (diff > 1) service.addLineQuantity(invoice, newLine, diff - 1); }
          }
        } else if (diff < 0 && existingLine) service.minusLineQuantity(invoice, existingLine, Math.abs(diff));
      }
    },

  },
});
